// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useSyncExternalStore } from "react";

// <Toaster> lives in __root, outside the assistant provider. The launcher publishes how
// much of the bottom-right corner it covers so toasts can step around it.

export type Footprint =
  | { kind: "hidden" }
  | { kind: "button" }
  | { kind: "panel"; width: number; viewportWidth: number }
  | { kind: "fullscreen" };

export const LAUNCHER_EDGE = 16;
export const LAUNCHER_GAP = 12;
export const LAUNCHER_BUTTON = 48;
/** Sonner's toast width. */
const TOAST_WIDTH = 356;

let current: Footprint = { kind: "hidden" };
const listeners = new Set<() => void>();

export function publishFootprint(next: Footprint): void {
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  current = next;
  for (const listener of listeners) listener();
}

export function useAssistantFootprint(): Footprint {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
}

type Offset = { right: number; bottom: number; left?: number };

export interface ToasterPlacement {
  position?: "top-right" | "top-center";
  offset?: Offset;
  /** Sonner ignores `offset` below 600px and reads this instead. */
  mobileOffset?: Offset;
}

export function toasterPropsFor(footprint: Footprint): ToasterPlacement {
  switch (footprint.kind) {
    case "hidden":
      return {};
    case "fullscreen":
      return { position: "top-center" };
    case "button": {
      const bottom = LAUNCHER_EDGE + LAUNCHER_BUTTON + LAUNCHER_GAP;
      return {
        offset: { right: LAUNCHER_EDGE, bottom },
        mobileOffset: { right: LAUNCHER_EDGE, bottom, left: LAUNCHER_EDGE },
      };
    }
    case "panel": {
      const right = LAUNCHER_EDGE + footprint.width + LAUNCHER_GAP;
      // No room beside the panel: the top edge beats landing on the composer.
      return footprint.viewportWidth - right >= TOAST_WIDTH + LAUNCHER_EDGE
        ? { offset: { right, bottom: LAUNCHER_EDGE } }
        : { position: "top-right" };
    }
  }
}
